import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Tabs, Tab, Chip,
  Card, CardContent, CardActionArea, Alert,
  IconButton, Grid, Skeleton, Paper, alpha, useTheme,
  Fade,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import DraftsIcon from '@mui/icons-material/Drafts';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import EastIcon from '@mui/icons-material/East';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AREA_STYLES, getAreaStyle, getAreaIcon } from './areaConfig';
import * as flowService from '../../services/documentFlowService';
import type { DocumentFlow, FlowDraft } from '../../services/documentFlowService';

const CATEGORY_TABS = [
  { value: '', label: 'Todos' },
  { value: 'pecas_juridicas', label: 'Peças Jurídicas' },
  { value: 'contratos', label: 'Contratos' },
  { value: 'extrajudicial', label: 'Extrajudicial' },
  { value: 'ferramentas', label: 'Ferramentas' },
];

// Busca e filtros sem diferença de acento/maiúsculas ("previdenciário" = "previdenciario")
const canon = (t?: string | null) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
// "contrato" (dado) x "contratos" (aba): compara sem o plural
const sameCategory = (c: string | undefined, tab: string) => canon(c).replace(/s$/, '') === tab.replace(/s$/, '');
const flowAreas = (f: any): string[] => Array.from(new Set([f.area, ...(f.areas || [])].map(canon).filter(Boolean)));
// Termos que o advogado digita e o nome do fluxo usa outro
const SEARCH_SYNONYMS: Record<string, string[]> = {
  'pensao': ['alimentos'],
  'alimenticia': ['alimentos'],
  'divorcio': ['divorcio', 'partilha'],
  'guarda': ['guarda'],
  'multa de transito': ['infracao', 'jari', 'cetran'],
  'multa': ['infracao', 'penalidade'],
  'inss': ['previdenciari', 'beneficio'],
  'aposentadoria': ['aposentadoria', 'previdenciari'],
  'despejo': ['despejo', 'locacao'],
  'aluguel': ['locacao', 'despejo'],
  'demissao': ['reclamacao trabalhista', 'rescis'],
  'trabalhista': ['trabalhista', 'reclamacao'],
  'liminar': ['tutela', 'liminar'],
  'defesa': ['contestacao', 'defesa'],
};
const searchTerms = (q: string): string[] => {
  const c = canon(q);
  if (!c) return [];
  const extra = Object.entries(SEARCH_SYNONYMS).filter(([k]) => c.includes(k)).flatMap(([, v]) => v);
  return [c, ...extra];
};

const FlowSelector: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const [flows, setFlows] = useState<DocumentFlow[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [recentFlows, setRecentFlows] = useState<DocumentFlow[]>([]);
  const [drafts, setDrafts] = useState<FlowDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState('');
  // Initialize area from URL param (e.g., /documents/flows?area=trabalhista)
  const [area, setArea] = useState(searchParams.get('area') || '');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [flowsData, favsData, recentData, draftsData] = await Promise.all([
        flowService.listActiveFlows(),
        flowService.listFavorites().catch(() => []),
        flowService.getRecentFlows(5).catch(() => []),
        flowService.listDrafts({ limit: 10 }).catch(() => ({ drafts: [], total: 0 })),
      ]);
      setFlows(flowsData);
      setFavorites(new Set(favsData.map((f: DocumentFlow) => f.id)));
      setRecentFlows(recentData);
      setDrafts(draftsData.drafts);
    } catch { setError('Erro ao carregar fluxos'); }
    finally { setLoading(false); }
  };

  const toggleFavorite = async (flowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (favorites.has(flowId)) {
        await flowService.removeFavorite(flowId);
        setFavorites(prev => { const next = new Set(prev); next.delete(flowId); return next; });
      } else {
        await flowService.addFavorite(flowId);
        setFavorites(prev => new Set(prev).add(flowId));
      }
    } catch {}
  };

  const filteredFlows = useMemo(() => {
    const terms = searchTerms(search);
    return flows.filter(f => {
      if (terms.length) {
        const haystack = canon(`${f.name} ${f.description || ''} ${(f.areas || []).join(' ')}`);
        if (!terms.some(t => haystack.includes(t))) return false;
      }
      if (category && !sameCategory(f.category, category)) return false;
      if (area && !flowAreas(f).includes(area)) return false;
      return true;
    });
  }, [flows, search, category, area]);

  // Available areas based on the current category filter
  const availableAreas = useMemo(() => {
    const base = category ? flows.filter(f => sameCategory(f.category, category)) : flows;
    const areaSet = new Set(base.flatMap(flowAreas));
    return Object.entries(AREA_STYLES).filter(([key]) => areaSet.has(key));
  }, [flows, category]);

  // Reset area filter when category changes and the current area is no longer available
  useEffect(() => {
    if (area && category) {
      const areasInCategory = flows.filter(f => sameCategory(f.category, category)).flatMap(flowAreas);
      if (!areasInCategory.includes(area)) setArea('');
    }
  }, [category]);

  const GENERIC_SLUG = 'documento-livre';
  const genericFlow = useMemo(() => flows.find(f => f.slug === GENERIC_SLUG), [flows]);
  const featuredFlows = useMemo(() => filteredFlows.filter(f => f.is_featured && f.slug !== GENERIC_SLUG), [filteredFlows]);
  const regularFlows = useMemo(() => filteredFlows.filter(f => !f.is_featured && f.slug !== GENERIC_SLUG), [filteredFlows]);
  const draftFlowIds = useMemo(() => new Set(drafts.map(d => d.flow_id)), [drafts]);

  const FlowCard: React.FC<{ flow: DocumentFlow; featured?: boolean }> = ({ flow, featured }) => {
    const areaConf = getAreaStyle(flow.area);
    return (
      <Card variant={featured ? 'elevation' : 'outlined'}
        elevation={featured ? 3 : 0}
        onMouseEnter={() => flowService.prefetchFlow(flow.slug)}
        sx={{
          height: '100%', position: 'relative', borderRadius: 3, overflow: 'visible',
          transition: 'all 0.25s ease',
          border: featured ? `2px solid ${alpha(areaConf.color, 0.3)}` : undefined,
          '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}` },
        }}>
        <CardActionArea onClick={() => navigate(`/documents/flow/${flow.slug}`)} sx={{ height: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(areaConf.color, 0.1),
              }}>
                {getAreaIcon(flow.area, 24)}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {featured && (
                  <Chip icon={<StarIcon sx={{ fontSize: 14 }} />} label="Destaque" size="small" color="primary"
                    sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                )}
                <IconButton component="div" size="small" onClick={(e) => toggleFavorite(flow.id, e)}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
                  {favorites.has(flow.id) ? <FavoriteIcon sx={{ color: 'primary.main' }} fontSize="small" /> : <FavoriteBorderIcon fontSize="small" sx={{ color: 'action.disabled' }} />}
                </IconButton>
              </Box>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.3 }}>{flow.name}</Typography>
            {flow.description && (
              <Typography variant="body2" color="text.secondary"
                sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                {flow.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 'auto' }}>
              <Chip label={areaConf.label} size="small" sx={{ bgcolor: alpha(areaConf.color, 0.1), color: areaConf.color, fontWeight: 600, fontSize: '0.7rem' }} />
              {draftFlowIds.has(flow.id) && (
                <Chip icon={<DraftsIcon sx={{ fontSize: '14px !important' }} />} label="Rascunho" size="small" color="warning" sx={{ fontWeight: 500, fontSize: '0.7rem' }} />
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const SectionHeader: React.FC<{ icon: React.ReactElement; title: string; count?: number }> = ({ icon, title, count }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}>
        {icon}
      </Box>
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      {count !== undefined && <Chip label={count} size="small" sx={{ fontWeight: 600 }} />}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Skeleton variant="text" width={300} height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={400} height={28} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={56} sx={{ mb: 2, borderRadius: 3 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <AutoAwesomeIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={800}>Criar Documento</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Selecione um fluxo inteligente para gerar seu documento jurídico com IA
        </Typography>
      </Box>

      {/* Generic Flow Hero Banner */}
      {genericFlow && !search && (
        <Card
          onMouseEnter={() => flowService.prefetchFlow(genericFlow.slug)}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute', top: -30, right: -20, opacity: 0.06 }}>
            <AutoAwesomeIcon sx={{ fontSize: 200 }} />
          </Box>
          <CardActionArea
            onClick={() => navigate(`/documents/flow/${genericFlow.slug}`)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0 }}
          >
            <CardContent sx={{ py: 3, px: 3.5, flex: 1, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: 2.5, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha('#fff', 0.15), backdropFilter: 'blur(4px)',
                }}>
                  <DescriptionIcon sx={{ fontSize: 24, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2 }}>
                    Não sabe qual documento criar?
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
                    Compartilhe o contexto, documentos ou áudio para a IA te ajudar
                  </Typography>
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  bgcolor: alpha('#fff', 0.2), color: '#fff',
                  fontWeight: 700, borderRadius: 2, px: 2.5, py: 0.75,
                  fontSize: '0.8125rem',
                  backdropFilter: 'blur(4px)',
                }}
              >
                Criar Documento Livre
                <EastIcon sx={{ fontSize: 16 }} />
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      )}

      {/* Search */}
      <TextField fullWidth placeholder="Buscar por nome ou descrição..." value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
          sx: { borderRadius: 3, bgcolor: 'background.paper' },
        }}
        sx={{ mb: 2 }}
      />

      {/* Category Tabs */}
      <Tabs value={category} onChange={(_, v) => setCategory(v)}
        sx={{ mb: 1.5, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 } }}>
        {CATEGORY_TABS.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      {/* Area Chips - only show areas that have flows in the current category */}
      {availableAreas.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 4 }}>
          <Chip label="Todas as áreas" variant={area === '' ? 'filled' : 'outlined'}
            sx={{
              fontWeight: area === '' ? 700 : 500, borderRadius: 2,
              bgcolor: area === '' ? 'primary.main' : undefined,
              color: area === '' ? 'white' : undefined,
              '&:hover': { bgcolor: area === '' ? 'primary.dark' : alpha(theme.palette.primary.main, 0.1) },
            }}
            onClick={() => setArea('')} />
          {availableAreas.map(([val, conf]) => (
            <Chip key={val} label={conf.label}
              variant={area === val ? 'filled' : 'outlined'}
              sx={{
                fontWeight: area === val ? 700 : 500, borderRadius: 2,
                bgcolor: area === val ? conf.color : undefined,
                color: area === val ? 'white' : undefined,
                borderColor: conf.color,
                '&:hover': { bgcolor: area === val ? conf.color : alpha(conf.color, 0.1) },
              }}
              onClick={() => setArea(area === val ? '' : val)}
            />
          ))}
        </Box>
      )}

      {/* Pending Drafts */}
      {drafts.length > 0 && !search && (
        <Fade in>
          <Box sx={{ mb: 4 }}>
            <SectionHeader icon={<DraftsIcon />} title="Rascunhos Pendentes" count={drafts.length} />
            <Grid container spacing={2}>
              {drafts.slice(0, 3).map(draft => {
                const pct = draft.total_steps ? ((draft.current_step + 1) / draft.total_steps) * 100 : 0;
                return (
                  <Grid item xs={12} sm={6} md={4} key={draft.id}>
                    <Card variant="outlined"
                      sx={{
                        borderRadius: 2.5, borderLeft: `4px solid ${theme.palette.warning.main}`,
                        bgcolor: alpha(theme.palette.warning.main, 0.03),
                        transition: 'all 0.2s ease',
                        '&:hover': { boxShadow: 2, transform: 'translateY(-2px)' },
                      }}>
                      <CardActionArea onClick={() => navigate(`/documents/flow/${draft.flow_slug}?draft=${draft.id}`)}>
                        <CardContent sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>{draft.flow_name}</Typography>
                            <ArrowForwardIcon fontSize="small" sx={{ ml: 'auto', color: 'action.disabled' }} />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Etapa {draft.current_step + 1}/{draft.total_steps || '?'}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {new Date(draft.updated_at).toLocaleDateString('pt-BR')}
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.warning.main, 0.15), borderRadius: 1, height: 4 }}>
                            <Box sx={{ width: `${pct}%`, bgcolor: 'warning.main', borderRadius: 1, height: '100%', transition: 'width 0.3s' }} />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Recent */}
      {recentFlows.length > 0 && !search && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader icon={<HistoryIcon />} title="Usados Recentemente" />
          <Grid container spacing={2}>
            {recentFlows.map(f => (
              <Grid item xs={12} sm={6} md={4} key={f.id}><FlowCard flow={f} /></Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Featured */}
      {featuredFlows.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader icon={<StarIcon />} title="Destaques" count={featuredFlows.length} />
          <Grid container spacing={2.5} sx={{ pt: 1.5 }}>
            {featuredFlows.map(f => (
              <Grid item xs={12} sm={6} md={4} key={f.id}><FlowCard flow={f} featured /></Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* All Flows */}
      <Box>
        <SectionHeader icon={<FolderOpenIcon />}
          title={search || category || area ? 'Resultados' : 'Todos os Fluxos'}
          count={regularFlows.length} />
        {regularFlows.length === 0 ? (
          <Paper variant="outlined"
            sx={{ py: 6, px: 3, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
            <FolderOpenIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>Nenhum fluxo encontrado</Typography>
            <Typography variant="body2" color="text.disabled">
              {search ? 'Ainda não temos um fluxo específico para isso.' : 'Nenhum fluxo disponível nesta categoria'}
            </Typography>
            {search && (
              <Button variant="contained" sx={{ mt: 2, borderRadius: 2 }}
                onClick={() => navigate(`/documents/flow/${GENERIC_SLUG}`)}>
                Criar com o Documento Livre
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {regularFlows.map(f => (
              <Grid item xs={12} sm={6} md={4} key={f.id}><FlowCard flow={f} /></Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default FlowSelector;
